/* eslint-disable no-mixed-spaces-and-tabs */
import { useCallback } from "react";
import useMapStore from "@/store/mapStore";
import useFeaturesStore from "@/store/featuresStore";
import { filterByAmenity, getSortedPOIs } from "@/lib/utils";
import { FilterItemModel } from "@/models/filterItem.model";
import Feature from "proximiio-js-library/lib/models/feature";
import centerOfMass from "@turf/center-of-mass";

export default function useNavigateToAmenity() {
  const map = useMapStore((state) => state.map);
  const gpsMode = useMapStore((state) => state.gpsMode);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const filterCategories = useMapStore((state) => state.filterCategories);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const features = useFeaturesStore((state) => state.features);
  const floors = useMapStore((state) => state.floors);
  const currentFloor = useMapStore((state) => state.currentFloor);
  const currentLang = useMapStore((state) => state.currentLang);
  const setActiveMenuItem = useMapStore((state) => state.setActiveMenuItem);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const centerToFinish = useMapStore((state) => state.centerToFinish);
  const setOverlayContent = useMapStore((state) => state.setOverlayContent);

  const pois = getSortedPOIs({ features, currentLang, floors });

  const navigateToFacility = useCallback(
    ({ amenityTitle }: { amenityTitle: string }) => {
      const amenity = filterCategories[
        filterCategories.findIndex((i) => i.id === "facilities")
      ].items.find((i) => i.title === amenityTitle);

      if (amenity) {
        if (!kioskMode && !gpsMode) {
          amenity.type = "list";
        }

        const filteredPois = filterByAmenity(
          pois.filter((feature) => feature.properties.type === "poi"),
          amenity.id,
        );

        if (!gpsMode && !kioskMode) {
          if (filteredPois.length === 1) {
            const feature = features.find((f) => f.id === filteredPois[0].id)!;
            setRouteFinish(feature);
            centerToFinish();
          }
          if (filteredPois.length > 1) {
            const sameLevelFeatures = filteredPois.filter(
              (i) => i.properties.level === currentFloor.level,
            );
            if (sameLevelFeatures.length === 0) {
              const floor = floors.find(
                (f) => f.level === filteredPois[0].properties.level,
              );
              if (floor) {
                map.setFloorById(floor.id);
              }
            }
            const centerPoint = centerOfMass({
              type: "FeatureCollection",
              features:
                sameLevelFeatures.length > 0 ? sameLevelFeatures : filteredPois,
            });
            if (mobileMode) {
              map.getMapboxInstance().jumpTo({
                center: centerPoint.geometry.coordinates as [number, number],
                zoom: 15.5,
                padding: {
                  top: 0,
                  bottom: 370,
                  left: 0,
                  right: 0,
                },
              });
            } else {
              map.getMapboxInstance().jumpTo({
                center: centerPoint.geometry.coordinates as [number, number],
              });
            }
          }
          setActiveFilter(amenity as FilterItemModel);
        } else {
          const closestFeature = map.getClosestFeature(
            amenity.id as string,
          ) as Feature;
          // setRouteStart("kiosk");
          setActiveFilter(amenity as FilterItemModel);
          setRouteFinish(closestFeature);
          centerToFinish();
        }
        setActiveMenuItem("map");
        setOverlayContent(undefined);
      }
    },
    [
      filterCategories,
      centerToFinish,
      currentFloor.level,
      features,
      floors,
      gpsMode,
      kioskMode,
      map,
      mobileMode,
      pois,
      setActiveFilter,
      setActiveMenuItem,
      setRouteFinish,
      setOverlayContent,
    ],
  );

  return [navigateToFacility];
}
